import { useDispatch, useSelector } from 'react-redux';
import { Dispatch, useCallback, useEffect } from 'react';
import debounce from 'lodash.debounce';

import { KnmCollapse } from '../../../KnmPackage/MainModule/components/KnmCollapse';
import { IGroupConditionForm } from '../interfaces/IGroupConditionForm';
import { PassportGroupPassportTypeEnum } from '../enums/PassportGroupPassportTypeEnum';
import { FieldBlock } from '../../../UIPackage/FormModule/components/FieldBlock';
import { SelectInputField } from '../../../UIPackage/MainModule/components/SelectInputField';
import { RootActions } from '../../../CorePackage/MainModule/types/RootActions';
import { catalogGetErvkObjectsOfControlSelectOptions } from '../../../CorePackage/CatalogsModule/actions/catalogsActions';
import {
  dictionariesFindSubjectKindSelectOptionsByQuery,
  dictionariesFindSubjectSubKindSelectOptionsByQuery,
  dictionariesItemValuesGet,
} from '../../../CorePackage/DictionariesModule/actions/dictionariesActions';
import { DictionaryNamesEnum } from '../../../CorePackage/DictionariesModule/enums/DictionaryNamesEnum';
import {
  getDictionaryDangerClassAllSelectOptions,
  getDictionaryRiskCategoryAllSelectOptions,
  getDictionarySubjectTypeAllSelectOptions,
} from '../../../CorePackage/DictionariesModule/selectors/dictionariesSelectors';

interface IObjectGroupConditionProps {
  name: string;
  category: IGroupConditionForm['category'];
}

export const ObjectGroupCondition = ({ name, category }: IObjectGroupConditionProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const objectTypeSelectOptions = useSelector(getDictionarySubjectTypeAllSelectOptions);
  const riskCategorySelectOptions = useSelector(getDictionaryRiskCategoryAllSelectOptions);
  const dangerClassSelectOptions = useSelector(getDictionaryDangerClassAllSelectOptions);

  useEffect(() => {
    dispatch(dictionariesItemValuesGet(DictionaryNamesEnum.SUBJECT_TYPE));
    dispatch(dictionariesItemValuesGet(DictionaryNamesEnum.RISK_CATEGORY));
  }, [dispatch]);

  const ervkObjectLoadOptions = useCallback(
    debounce((inputValue: string, callback: (fn: unknown) => void) => {
      if (inputValue.length < 3) {
        callback([]);
      } else {
        dispatch(catalogGetErvkObjectsOfControlSelectOptions(inputValue, callback));
      }
    }, 1000),
    [dispatch],
  );

  const subjectKindLoadOptions = useCallback(
    debounce((inputValue: string, callback: (fn: unknown) => void) => {
      if (inputValue.length < 5) {
        callback([]);
      } else {
        dispatch(dictionariesFindSubjectKindSelectOptionsByQuery(inputValue, callback));
      }
    }, 1000),
    [dispatch],
  );

  const subjectSubKindLoadOptions = useCallback(
    debounce((inputValue: string, callback: (fn: unknown) => void) => {
      if (inputValue.length < 5) {
        callback([]);
      } else {
        dispatch(dictionariesFindSubjectSubKindSelectOptionsByQuery(inputValue, callback));
      }
    }, 1000),
    [dispatch],
  );

  if (category === PassportGroupPassportTypeEnum.ERP) {
    return null;
  }

  return (
    <KnmCollapse title="Сведения об объектах контроля мероприятий">
      <>
        <FieldBlock label="Категорированный объект контроля ЕРВК" margin="bottom">
          <SelectInputField
            name={`${name}.ervkNumbers`}
            isMulti
            isClearable
            isAsync
            isSearchable
            loadOptions={ervkObjectLoadOptions}
          />
        </FieldBlock>
        <FieldBlock label="Тип объекта контроля" margin="bottom">
          <SelectInputField
            name={`${name}.objectTypes`}
            options={objectTypeSelectOptions}
            isSearchable
            isMulti
            isClearable
          />
        </FieldBlock>
        <FieldBlock label="Вид объекта контроля" margin="bottom">
          <SelectInputField
            name={`${name}.objectKinds`}
            isMulti
            isClearable
            isAsync
            isSearchable
            loadOptions={subjectKindLoadOptions}
          />
        </FieldBlock>
        <FieldBlock label="Подвид объекта контроля" margin="bottom">
          <SelectInputField
            name={`${name}.objectSubKinds`}
            isMulti
            isClearable
            isAsync
            isSearchable
            loadOptions={subjectSubKindLoadOptions}
          />
        </FieldBlock>
        <FieldBlock label="Категория риска объекта контроля" margin="bottom">
          <SelectInputField name={`${name}.riskCategories`} options={riskCategorySelectOptions} isMulti isClearable />
        </FieldBlock>
        <FieldBlock label="Класс опасности объекта контроля" margin="bottom">
          <SelectInputField name={`${name}.dangerClasses`} options={dangerClassSelectOptions} isMulti isClearable />
        </FieldBlock>
      </>
    </KnmCollapse>
  );
};
